import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useSelector, useDispatch } from 'react-redux';
import { isDarkMode } from '../../../utils/helpers';
import Panel from '../../../atoms/panels/Panel';
import OpeningOfferModal from '../../common/modals/OpeningOfferModal';

import typography from '../../../theme/typography';
import { useDisableBodyElementScroll } from '../../../hooks/useDisableBodyScroll';
import EditableOpeningOfferModal from '../../common/modals/EditableOpeningOfferModal';
import { AccountMaker } from '../../../models/Account';
import { ProfileMaker } from '../../../models/Profile';
import { useSearchParams } from 'react-router-dom';
import { getDefaultOfferAmount } from '../../../utils/offers';
import { updateProfileOpeningOffer } from '../../../actions/profileActions';
import { RootState } from '../../../store';
import { FONT_FAMILIES } from 'theme/material/constants';
import { getTextColor } from 'style';

const OpeningOfferWrapper = styled('div')({});

const OpeningAmountLabel = styled('span')(() => ({
  fontSize: 26,
  fontWeight: 700,
  lineHeight: '27px',
  letterSpacing: '-0.3px',
  color: isDarkMode() ? typography['dark'].primaryText : '#3E79A5',
}));

const OpeningOffer = (): any => {
  const dispatch = useDispatch();
  const account = useSelector((state: RootState) => AccountMaker.create(state.profile));
  const profileModel = ProfileMaker.create(account?.data?.profile);
  const defaultOfferPrice = profileModel?.profile.data.default_offer_amount ?? getDefaultOfferAmount();

  const [searchParams] = useSearchParams();

  const [toggleEditOpeningOffer, setToggleEditOpeningOffer] = useState(false);
  const [showOpeningOffer, setShowOpenningOffer] = useState(false);
  const [offerPrice, setOfferPrice] = useState(defaultOfferPrice);

  useDisableBodyElementScroll(showOpeningOffer || toggleEditOpeningOffer);

  useEffect(() => {
    const action = searchParams.get('action');

    if (action && action === 'editOpeningOffer' && toggleEditOpeningOffer === false) {
      setToggleEditOpeningOffer(true);
    }
  }, []);

  const currencySymbols = useMemo(() => {
    if (!account?.data.currency) {
      return null;
    }

    return {
      currencyInitial: account.data.currency.data.initial,
      currencyLabel: String.fromCharCode(Number(account.data.currency.data.label_unicode)),
    };
  }, [account?.data?.currency]);

  const toggleEditable = e => {
    e.preventDefault();

    // remove action query param from url
    if (searchParams.has('action')) {
      const url = new URL(window.location.href);

      url.searchParams.delete('action');
      history.replaceState({ hashId: profileModel.hashId() }, '', url.toString());
    }

    setToggleEditOpeningOffer(prev => !prev);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    toggleEditable(e);
    dispatch(
      updateProfileOpeningOffer({
        default_offer_amount: offerPrice,
      })
    );
  };

  const handleCancel = (e: any) => {
    toggleEditable(e);
    setOfferPrice(defaultOfferPrice);
  };

  if (!currencySymbols) {
    return null;
  }

  return (
    <>
      <OpeningOfferWrapper>
        <Panel
          editable
          withHeader
          title="My Opening Offer"
          className="profile__opening-offer"
          toggleEditable={toggleEditable}
          panelStyle="borderless"
          style={{ marginBottom: 15, padding: '0 15px 15px' }}
          customComponent={
            <OpeningOfferModal
              iconOnly
              linkStyle={{ marginLeft: 4 }}
              showOpeningOffer={showOpeningOffer}
              setShowOpenningOffer={setShowOpenningOffer}
              openEditOffer={() => {
                setToggleEditOpeningOffer(true);
                setShowOpenningOffer(false);
              }}
              currencySymbols={currencySymbols}
            />
          }
          customPanelHeaderStyle={{ justifyContent: 'flex-start' }}
          customTitleStyle={{
            fontWeight: 400,
            fontFamily: FONT_FAMILIES.secondary,
            lineHeight: '20px',
            letterSpacing: -0.2,
            ...getTextColor().DARK_4,
          }}
        >
          {currencySymbols.currencyLabel && (
            <OpeningAmountLabel>{`${currencySymbols.currencyInitial}${currencySymbols.currencyLabel}${defaultOfferPrice}`}</OpeningAmountLabel>
          )}
        </Panel>
      </OpeningOfferWrapper>
      {toggleEditOpeningOffer && (
        <EditableOpeningOfferModal
          title="My Opening Offer"
          handleSubmit={handleSubmit}
          handleCancel={handleCancel}
          currencySymbols={currencySymbols}
          offerPrice={offerPrice}
          setOfferPrice={setOfferPrice}
          toggleEditOpeningOffer={toggleEditOpeningOffer}
        />
      )}
    </>
  );
};

export default React.memo(OpeningOffer);
